import { create } from 'zustand';
import { resetRealtime } from '@/hooks/useSSE';
import { queryClient } from '@/lib/queryClient';
import { User, LoginResponse } from '@/lib/types';
import api, { auth as authApi } from '@/lib/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isOffline: boolean;
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginOffline: () => void;
  logout: () => Promise<void>;
  revokeCurrentToken: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  setTokens: (accessToken: string) => void;
  refreshSession: () => Promise<boolean>;
  init: () => Promise<void>;
}

/**
 * Session state is IN MEMORY ONLY.
 *
 * The store used to be wrapped in `persist`, which wrote the access token AND the
 * 30-day refresh token to localStorage under `auth-storage`. Anything running in
 * the page -- an injected script, or a single compromised transitive npm
 * dependency -- could read them and mint sessions far beyond the tab's lifetime.
 * The refresh token now lives in an HttpOnly cookie the server sets
 * (Path=/api/auth, SameSite=Strict), and the access token is never written to
 * storage: a reload re-establishes the session through the cookie via
 * refreshSession(). XSS can no longer exfiltrate a durable credential.
 */
let activeRefreshPromise: Promise<boolean> | null = null;

function setAuthHeader(token: string | null) {
  try {
    if (api?.defaults?.headers?.common) {
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        delete api.defaults.headers.common['Authorization'];
      }
    }
  } catch {}
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isOffline: false,
  ready: false,

  login: async (email: string, password: string) => {
    const res: LoginResponse = await authApi.login(email, password);
    const user: User = {
      id: res.user.id,
      email: res.user.email,
      role: res.user.role as User['role'],
    };
    try {
      sessionStorage.setItem('hiregen_access_token', res.access_token);
      sessionStorage.setItem('hiregen_user', JSON.stringify(user));
      sessionStorage.removeItem('hiregen_offline_mode');
      localStorage.setItem('hiregen_access_token', res.access_token);
      localStorage.setItem('hiregen_user', JSON.stringify(user));
      localStorage.removeItem('hiregen_offline_mode');
    } catch {}
    const { saveOfflineSession } = await import('@/lib/offlineStorage');
    saveOfflineSession(user);
    // Cache from a previous identity (or a logged-out session) must not leak
    // into this one: it would render the last user's leads/contacts.
    queryClient.clear();
    set({ user, token: res.access_token, isAuthenticated: true, isOffline: false, ready: true });
    setAuthHeader(res.access_token);
  },

  loginOffline: async () => {
    const { getOfflineSession } = await import('@/lib/offlineStorage');
    const offlineUser = getOfflineSession();
    if (!offlineUser) {
      set({ user: null, token: null, isAuthenticated: false, isOffline: false, ready: true });
      setAuthHeader(null);
      return;
    }
    try {
      sessionStorage.setItem('hiregen_offline_mode', 'true');
      sessionStorage.setItem('hiregen_access_token', 'offline-token');
      sessionStorage.setItem('hiregen_user', JSON.stringify(offlineUser));
      localStorage.setItem('hiregen_offline_mode', 'true');
      localStorage.setItem('hiregen_access_token', 'offline-token');
      localStorage.setItem('hiregen_user', JSON.stringify(offlineUser));
    } catch {}
    set({ user: offlineUser, token: 'offline-token', isAuthenticated: true, isOffline: true, ready: true });
    setAuthHeader(null);
  },

  logout: async () => {
    try {
      sessionStorage.removeItem('hiregen_access_token');
      sessionStorage.removeItem('hiregen_user');
      sessionStorage.removeItem('hiregen_offline_mode');
      localStorage.removeItem('hiregen_access_token');
      localStorage.removeItem('hiregen_user');
      localStorage.removeItem('hiregen_offline_mode');
      localStorage.removeItem('recruitus_last_known_user');
    } catch {}
    try {
      const { clearOfflineSession } = await import('@/lib/offlineStorage');
      clearOfflineSession();
    } catch {}
    set({ user: null, token: null, isAuthenticated: false, isOffline: false, ready: true });
    setAuthHeader(null);
    // Expire every cached query with the session: the next login must not see
    // this user's leads/contacts.
    queryClient.clear();
    resetRealtime();
  },

  revokeCurrentToken: async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore -- local teardown ends the session
    }
  },

  register: async (email: string, password: string) => {
    await authApi.register(email, password);
    await get().login(email, password);
  },

  setTokens: (accessToken: string) => {
    try {
      sessionStorage.setItem('hiregen_access_token', accessToken);
      localStorage.setItem('hiregen_access_token', accessToken);
    } catch {}
    set({ token: accessToken });
    setAuthHeader(accessToken);
  },

  refreshSession: async () => {
    if (activeRefreshPromise) {
      return activeRefreshPromise;
    }
    activeRefreshPromise = (async () => {
      try {
        const res = await api.post('/auth/refresh');
        const payload = res.data as LoginResponse;
        const user: User | null = payload.user
          ? {
              id: payload.user.id,
              email: payload.user.email,
              role: payload.user.role as User['role'],
            }
          : get().user;
        if (user) {
          const { saveOfflineSession } = await import('@/lib/offlineStorage');
          saveOfflineSession(user);
        }
        try {
          sessionStorage.setItem('hiregen_access_token', payload.access_token);
          localStorage.setItem('hiregen_access_token', payload.access_token);
          if (user) {
            sessionStorage.setItem('hiregen_user', JSON.stringify(user));
            localStorage.setItem('hiregen_user', JSON.stringify(user));
          }
          sessionStorage.removeItem('hiregen_offline_mode');
          localStorage.removeItem('hiregen_offline_mode');
        } catch {}
        set({ user, token: payload.access_token, isAuthenticated: true, isOffline: false, ready: true });
        setAuthHeader(payload.access_token);
        return true;
      } catch (err: any) {
        // If network unreachable / server completely down, ONLY degrade to offline mode
        // if this device already had an active, authenticated user session.
        // Unauthenticated visitors or new devices must NEVER be granted access.
        const isNetworkOrServerDown = !err?.response || [502, 503, 504].includes(err?.response?.status);
        const currentUser = get().user;
        if (isNetworkOrServerDown && currentUser) {
          const { getOfflineSession } = await import('@/lib/offlineStorage');
          const offlineUser = getOfflineSession();
          if (offlineUser && offlineUser.id === currentUser.id) {
            try {
              sessionStorage.setItem('hiregen_offline_mode', 'true');
              localStorage.setItem('hiregen_offline_mode', 'true');
            } catch {}
            set({ user: offlineUser, token: 'offline-token', isAuthenticated: true, isOffline: true, ready: true });
            setAuthHeader(null);
            return true;
          }
        }
        try {
          sessionStorage.removeItem('hiregen_access_token');
          sessionStorage.removeItem('hiregen_user');
          sessionStorage.removeItem('hiregen_offline_mode');
          localStorage.removeItem('hiregen_access_token');
          localStorage.removeItem('hiregen_user');
          localStorage.removeItem('hiregen_offline_mode');
        } catch {}
        set({ user: null, token: null, isAuthenticated: false, isOffline: false, ready: true });
        setAuthHeader(null);
        return false;
      } finally {
        activeRefreshPromise = null;
      }
    })();
    return activeRefreshPromise;
  },

  init: async () => {
    if (get().ready) return;
    if (get().token) {
      if (get().token === 'offline-token') {
        const { getOfflineSession } = await import('@/lib/offlineStorage');
        const offlineUser = getOfflineSession();
        if (!offlineUser) {
          set({ user: null, token: null, isAuthenticated: false, isOffline: false, ready: true });
          setAuthHeader(null);
          return;
        }
      }
      set({ isAuthenticated: true, ready: true });
      if (get().token !== 'offline-token') {
        setAuthHeader(get().token);
      }
      return;
    }
    // Check sessionStorage and localStorage
    try {
      const storedToken = sessionStorage.getItem('hiregen_access_token') || localStorage.getItem('hiregen_access_token');
      const storedUser = sessionStorage.getItem('hiregen_user') || localStorage.getItem('hiregen_user');
      if (storedToken && storedUser) {
        const user = JSON.parse(storedUser);
        const isOfflineMode = storedToken === 'offline-token';
        if (isOfflineMode) {
          const { getOfflineSession } = await import('@/lib/offlineStorage');
          const offlineUser = getOfflineSession();
          if (!offlineUser || offlineUser.id !== user?.id) {
            sessionStorage.removeItem('hiregen_access_token');
            sessionStorage.removeItem('hiregen_user');
            sessionStorage.removeItem('hiregen_offline_mode');
            localStorage.removeItem('hiregen_access_token');
            localStorage.removeItem('hiregen_user');
            localStorage.removeItem('hiregen_offline_mode');
            await get().refreshSession();
            return;
          }
        } else {
          sessionStorage.removeItem('hiregen_offline_mode');
          localStorage.removeItem('hiregen_offline_mode');
        }
        set({ user, token: storedToken, isAuthenticated: true, isOffline: isOfflineMode, ready: true });
        if (!isOfflineMode) {
          setAuthHeader(storedToken);
        }
        return;
      }
    } catch {}
    await get().refreshSession();
  },
}));
